import axios from "axios";
import router from "./router";
import {
  buildSessionExpiredError,
  clearStoredSession,
  getStoredAccessData,
  hasValidSession,
  isSessionExpiredError,
} from "./publicjs/authSession";

const REQUEST_TIMEOUT = Number(process.env.VUE_APP_API_TIMEOUT || 60000);
const PUBLIC_REQUEST_PATHS = ["login", "empresa", "turnos", "taller/publico"];

const isPublicRequest = (config = {}) => {
  const requestUrl = String(config.url || "").replace(/^\/+/, "");

  return PUBLIC_REQUEST_PATHS.some(
    (publicPath) =>
      requestUrl === publicPath || requestUrl.startsWith(`${publicPath}/`),
  );
};

// Crear una instancia de axios
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: REQUEST_TIMEOUT,
});

// Agregar un interceptor de solicitudes
api.interceptors.request.use((config) => {
  const token = getStoredAccessData();
  const publicRequest = isPublicRequest(config);
  const currentRoute = router.currentRoute.value;
  const routePath = currentRoute?.path || window.location.pathname || "";
  const routeTitle =
    currentRoute?.meta?.title || document.title || currentRoute?.name || "";
  const routeModule = routePath.split("/").filter(Boolean)[0] || "home";

  if (token && hasValidSession()) {
    config.headers.Authorization = `Bearer ${token.token}`;
  } else if (token) {
    clearStoredSession();

    if (publicRequest) {
      return config;
    }

    if (router.currentRoute.value.path !== "/login") {
      router.push("/login");
    }

    return Promise.reject(buildSessionExpiredError());
  }

  config.headers["X-Client-Route"] = routePath;
  config.headers["X-Client-Screen"] = routeTitle;
  config.headers["X-Client-Module"] = routeModule;

  return config;
});

// Interceptor de respuesta - Manejo automático de sesiones inválidas
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isSessionExpiredError(error)) {
      return Promise.reject(error);
    }

    const isTimeoutError =
      error.code === "ECONNABORTED" ||
      String(error.message || "")
        .toLowerCase()
        .includes("timeout");

    if (isTimeoutError) {
      error.message =
        "El servidor tardó demasiado en responder. Intenta de nuevo.";
    } else if (!error.response && !error.message) {
      error.message = "No se pudo conectar con el backend.";
    }

    const responseStatus = error.response?.status;
    const authStatus = error.response?.data?.status;
    const isAuthenticationFailure =
      responseStatus === 401 &&
      ["Expired token", "Invalid token", "Token not found"].includes(
        authStatus,
      );

    if (isAuthenticationFailure) {
      clearStoredSession();

      // Redirigir al login solo si no estamos ya ahí
      if (router.currentRoute.value.path !== "/login") {
        router.push("/login");
      }

      return Promise.reject(buildSessionExpiredError());
    }

    return Promise.reject(error);
  },
);

export default api;
